<!-- =========================================================================================
    File Name: 2VPageLayout.vue
    Description: This layout serves as the primary layout for displaying Version 2 (V2) pages within an iframe, ensuring a seamless integration of V2 content into the VueJS Dashboard. Designed to maintain the core UI components such as the navbar, sidebar, and footer, while dedicating the main content area to iframe-based V2 content display. Ideal for applications requiring backward compatibility or phased migration to new versions.
    ----------------------------------------------------------------------------------------
    Item Name: Vuesax Admin - VueJS Dashboard Admin Template
    Author: Pixinvent
    Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
  <div id="v2_page_layout" class="v2_page_layout_container">
    <!-- Header -->
    <v2-header
      :is-sidebar-open="isSidebarOpen"
      :toggle-sidebar="toggleSidebar"
      :is-mobile="isMobile"
      :company-logo="companyLogo"
    ></v2-header>

    <div class="v2_page_layout_wrapper">
      <!-- Sidebar -->
      <v2-sidebar
        :is-sidebar-open="isSidebarOpen"
        :toggle-sidebar="toggleSidebar"
        :is-mobile="isMobile"
        :company-logo="companyLogo"
      ></v2-sidebar>

      <div class="v2_page_layout_content" ref="scrollContent">
        <!-- BREADCRUMB -->
        <vx-breadcrumb
          class="ml-4 md:block hidden"
          v-if="$route.meta.breadcrumb"
        />
        <button
          @click="scrollToTop"
          class="top-back-button"
          v-if="showBackToTopButton"
        >
          <arrow-up-icon class="icon-size"></arrow-up-icon>
        </button>

        <transition :name="routerTransition" mode="out-in">
          <router-view></router-view>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import themeConfig from '@/../themeConfig.js';
import V2Header from './components/V2Header.vue';
import V2Sidebar from './components/V2Sidebar.vue';
import { ArrowUpIcon } from 'vue-feather-icons';
import functions from '../utils/functions';

export default {
  components: {
    V2Header,
    V2Sidebar,
    ArrowUpIcon,
  },

  computed: {
    companyLogo: {
      get() {
        return this.$store.state.logo;
      },
      set() {},
    },
  },

  data() {
    return {
      routerTransition: themeConfig.routerTransition || 'none',
      isMobile: false,
      isSidebarOpen: true,
      showBackToTopButton: false,
    };
  },

  methods: {
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;
      this.saveSidebarState();
    },

    saveSidebarState() {
      localStorage.setItem('isSidebarOpen', this.isSidebarOpen);
    },

    loadSidebarState() {
      const sidebarState = localStorage.getItem('isSidebarOpen');
      this.isSidebarOpen =
        sidebarState === null ? true : sidebarState === 'true';
    },

    checkIfMobile() {
      this.isMobile = window.matchMedia('(max-width: 768px)').matches;
    },

    getCompanyLogo() {
      const desktopDefaultLogo =
        'https://bboneapp.s3.amazonaws.com/v2/Logo_BP.png';
      const mobileDefaultLogo =
        'https://bboneapp.s3.amazonaws.com/v2/Logo_BP_Mobile.png';
      const defaultLogo = this.isMobile
        ? mobileDefaultLogo
        : desktopDefaultLogo;
      this.companyLogo = localStorage.getItem('logo') || defaultLogo;
    },

    initializeComponent() {
      this.loadSidebarState();
      this.checkIfMobile();
      this.getCompanyLogo();
    },

    scrollToTop() {
      this.$refs.scrollContent.scrollTop = 0;
    },

    checkScroll() {
      this.showBackToTopButton = this.$refs.scrollContent.scrollTop > 200;
    },
  },

  created() {
    const companyId = localStorage.getItem('idEmpresa');
    this.$i18n.locale = functions.setLocaleBasedOnCompany(Number(companyId));
  },

  mounted() {
    this.initializeComponent();
    window.addEventListener('resize', this.checkIfMobile);
    this.$refs.scrollContent.addEventListener('scroll', this.checkScroll);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.checkIfMobile);
    if (this.$refs.scrollContent) {
      this.$refs.scrollContent.removeEventListener('scroll', this.checkScroll);
    }
  },
};
</script>

<style lang="scss">
.v2_page_layout_container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
  background-color: #082440;
}

.v2_page_layout_wrapper {
  display: flex;
  flex: 1;
  width: 100%;
  overflow: hidden;
}

.v2_page_layout_content {
  flex: 1;
  background-color: #f8f8f8;
  border-top-left-radius: 1.5rem;
  overflow: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 32px;

  &::-webkit-scrollbar {
    display: none;
  }

  &:has(> .v2_page_loader) {
    padding: 0;
  }

  @media (max-width: 768px) {
    padding: 24px 8px;
    border-radius: 0dvi;
  }
}

.top-back-button {
  position: fixed;
  bottom: 80px;
  right: 8px;
  padding: 10px;
  background-color: #082440;
  color: white;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  z-index: 99;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background-color: darken(#082440, 5%);
  }
}

.icon-size {
  width: 20px;
  height: 20px;
  stroke: white;
}
</style>
